/**
 * What a roster is about to cost the verdict, said while it can still be changed.
 *
 * The generator reports both of these already, but it reports them in the result, which is
 * after the wait and after the score is on the board. They are properties of the roster
 * alone -- who is playing, and how many -- so they are knowable at the moment the player is
 * added, and a board that knows a verdict will be silent owes the reader that before the run
 * rather than in the post-mortem.
 *
 * These mirror `rk_registers.roster_problems` and must keep saying the same thing it says.
 * The two checks it makes that are not here -- a name that reads back as another instrument,
 * an instrument with no charted compass -- cannot happen from this picker, which offers only
 * the seats the pipeline itself named.
 */

import { GUITAR, type Seat } from "@app/shared-types";

/** The book's own floor, as `rk_balance.MINIMUM_PITCHES` states it. */
export const BALANCE_PITCHES = 3;

/** The families in a roster, an unknown instrument counting as a family of its own. */
function familiesOf(roster: readonly string[], seats: readonly Seat[]): string[] {
  const found = new Set<string>();
  for (const instrument of roster) {
    const seat = seats.find((one) => one.instrument === instrument);
    found.add(seat?.family ?? "instruments the book does not group");
  }
  return [...found].sort();
}

/**
 * Everything this roster will silence, in the generator's own words, or an empty list.
 *
 * An empty roster says nothing: the question has not been answered yet, and the Run button
 * already says so.
 */
export function rosterNotices(roster: readonly string[], seats: readonly Seat[]): string[] {
  if (roster.length === 0) return [];
  const notices: string[] = [];
  const families = familiesOf(roster, seats);
  if (families.length > 1) {
    const named =
      families.length > 2
        ? `${families.slice(0, -1).join(", ")} and ${families[families.length - 1]}`
        : families.join(" and ");
    notices.push(
      `This roster mixes ${named}, and the book weighs strength inside one group only, ` +
        "so the balance rule will stay silent over the score and the verdict will be " +
        "judged on the other rules alone.",
    );
  }
  const outside = seats.filter((seat) => !seat.book && roster.includes(seat.instrument));
  if (outside.length > 0) {
    notices.push(
      `${outside.map((seat) => seat.label).join(", ")}: not in the book, which gives no ` +
        "strength to weigh, so the balance rule will stay silent over every chord played in.",
    );
  }
  // A guitar sounds a chord alone, so a count of players says nothing of the rule there.
  if (roster.length < BALANCE_PITCHES && !roster.includes(GUITAR)) {
    notices.push(
      `${roster.length} player(s): the balance rule wants ${BALANCE_PITCHES} sounding ` +
        "pitches before it will weigh a chord, so it will stay silent over the score.",
    );
  }
  return notices;
}
